import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import {
  clearChargersAt, selectAllChargersAt, toggleChargerSelection, toggleLocationSelection,
  type ScopeSelection,
} from '@/lib/scopeSelection';
import styles from './ScopePicker.module.css';

/**
 * Station + charger picker. Pure presentational — caller fetches
 * the available locations/chargers and owns the selection state.
 *
 * Two consumers today:
 *   1. AccessPage detail editor (Phase 2.6) — managing scope for
 *      an existing user.
 *   2. UsersPage invite modal (Phase B commit 4) — picking initial
 *      scope at invite time for a third_party_admin role.
 *
 * Both surfaces hand the picker a snapshot of the tenant's
 * available stations + chargers and two Sets representing the
 * current draft selection. The picker emits change callbacks; the
 * caller persists.
 *
 * The "select all chargers in this station" / "clear" shortcuts
 * mirror v1's per-row UX. Selecting a station alone doesn't grant
 * charger access; the user picks chargers explicitly so the visual
 * state always matches the persisted scope.
 *
 * A granted charger always brings its station (Warren, 2026-09-28):
 * "'All' chargers adds the location. Ticking any charger adds its
 * location. A grant whose chargers have no location sees nothing, and
 * the picker should make that impossible rather than the resolver
 * silent about it." The scope resolver ANDs the two lists, so a charger
 * whose station is not granted resolves to zero rows. So ticking a
 * charger, or "All", ticks its station; unticking a station clears its
 * chargers (rules in @/lib/scopeSelection, where withChargerLocations()
 * also repairs a draft loaded from an older grant). PUT
 * /admin/access/:userId and the invite route refuse the mismatch too
 * (CHARGER_LOCATION_NOT_GRANTED).
 */

export interface ScopePickerLocation {
  _id: string;
  name: string;
}

export interface ScopePickerCharger {
  _id: string;
  identity: string;
  location_id: string;
}

export interface ScopePickerProps {
  /** Tenant-scoped list of pickable locations. Caller fetches.
   *  Empty array renders the "no locations yet" empty state. */
  availableLocations: ScopePickerLocation[];
  /** Tenant-scoped chargers. Each has location_id pointing into
   *  availableLocations; chargers with an unknown location_id
   *  are silently skipped (defensive — should never happen if
   *  caller fetches both from the same tenant). */
  availableChargers: ScopePickerCharger[];
  /** Currently-selected location ids. Caller's state. */
  selectedLocations: Set<string>;
  /** Currently-selected charger ids. */
  selectedChargers: Set<string>;
  /** Replace-the-whole-set callbacks; caller decides how to
   *  persist. The picker never mutates the props. */
  onLocationsChange: (next: Set<string>) => void;
  onChargersChange: (next: Set<string>) => void;
  /** Disables every checkbox + link button (e.g. while saving). */
  disabled?: boolean;
  /** Optional copy at the top of the picker. The two consumers
   *  use distinct intros (AccessPage's framing vs invite's
   *  "where can they work?" framing); the picker stays
   *  copy-agnostic and renders whatever's passed. */
  intro?: React.ReactNode;
  /** When true, renders the bottom "X stations · Y chargers
   *  selected. Save replaces the entire scope." footer. The
   *  invite modal hides this (the modal's own actions row
   *  carries the save semantics). */
  showSummaryFooter?: boolean;
}

export function ScopePicker({
  availableLocations,
  availableChargers,
  selectedLocations,
  selectedChargers,
  onLocationsChange,
  onChargersChange,
  disabled = false,
  intro,
  showSummaryFooter = false,
}: ScopePickerProps) {
  // Group chargers by location for the per-station nested layout.
  // Memoized on inputs so re-renders don't rebuild the map on
  // every checkbox toggle.
  const chargersByLocation = useMemo(() => {
    const map = new Map<string, ScopePickerCharger[]>();
    for (const c of availableChargers) {
      const list = map.get(c.location_id) ?? [];
      list.push(c);
      map.set(c.location_id, list);
    }
    return map;
  }, [availableChargers]);

  // Every change goes through @/lib/scopeSelection so a selected charger's
  // station is always selected; both callbacks fire only for what changed.
  const apply = (next: { locations: Set<string>; chargers: Set<string> }): void => {
    const sameSet = (x: ReadonlySet<string>, y: ReadonlySet<string>) =>
      x.size === y.size && [...x].every((v) => y.has(v));
    if (!sameSet(next.chargers, selectedChargers)) onChargersChange(next.chargers);
    if (!sameSet(next.locations, selectedLocations)) onLocationsChange(next.locations);
  };
  const sel: ScopeSelection = { locations: selectedLocations, chargers: selectedChargers };
  const idsAt = (locationId: string): string[] =>
    (chargersByLocation.get(locationId) ?? []).map((c) => c._id);

  const toggleLocation = (id: string): void => apply(toggleLocationSelection(sel, id, idsAt(id)));
  const toggleCharger = (id: string, locationId: string): void =>
    apply(toggleChargerSelection(sel, id, locationId));
  const selectAllChargersIn = (locationId: string): void =>
    apply(selectAllChargersAt(sel, locationId, idsAt(locationId)));
  const deselectAllChargersIn = (locationId: string): void =>
    apply(clearChargersAt(sel, idsAt(locationId)));

  if (availableLocations.length === 0) {
    return (
      <div className={styles.emptyState}>
        No locations to scope to yet. Create a location first, or
        skip scope and assign it later from Access Management.
      </div>
    );
  }

  return (
    <>
      {intro && <div className={styles.editorIntro}>{intro}</div>}

      <div className={styles.stationGrid}>
        {availableLocations.map((loc) => {
          const stationChargers = chargersByLocation.get(loc._id) ?? [];
          const stationSelected = selectedLocations.has(loc._id);
          const allChargersSelected = stationChargers.length > 0
            && stationChargers.every((c) => selectedChargers.has(c._id));
          return (
            <section key={loc._id} className={styles.stationCard}>
              <header className={styles.stationHeader}>
                <label className={styles.stationLabel}>
                  <input
                    type="checkbox"
                    checked={stationSelected}
                    onChange={() => toggleLocation(loc._id)}
                    disabled={disabled}
                  />
                  <span className={styles.stationName}>{loc.name}</span>
                </label>
                <div className={styles.stationActions}>
                  {stationChargers.length > 0 && !allChargersSelected && (
                    <button
                      type="button"
                      className={styles.linkBtn}
                      onClick={() => selectAllChargersIn(loc._id)}
                      disabled={disabled}
                    >
                      <Plus size={11} /> All {stationChargers.length}
                    </button>
                  )}
                  {stationChargers.length > 0 && allChargersSelected && (
                    <button
                      type="button"
                      className={styles.linkBtn}
                      onClick={() => deselectAllChargersIn(loc._id)}
                      disabled={disabled}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </header>
              {stationChargers.length === 0 ? (
                <p className={styles.stationEmpty}>No chargers at this station yet.</p>
              ) : (
                <ul className={styles.chargerList}>
                  {stationChargers.map((c) => (
                    <li key={c._id} className={styles.chargerRow}>
                      <label className={styles.chargerLabel}>
                        <input
                          type="checkbox"
                          checked={selectedChargers.has(c._id)}
                          onChange={() => toggleCharger(c._id, loc._id)}
                          disabled={disabled}
                        />
                        <span className={styles.chargerIdentity}>{c.identity}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      {showSummaryFooter && (
        <div className={styles.summaryFooter}>
          <span>
            {selectedLocations.size} station{selectedLocations.size === 1 ? '' : 's'}
            {' · '}
            {selectedChargers.size} charger{selectedChargers.size === 1 ? '' : 's'} selected
          </span>
          <span className={styles.summaryHint}>
            Save replaces this user&rsquo;s entire scope.
          </span>
        </div>
      )}
    </>
  );
}
